import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/ui";
import { AssetTableItem } from "../../../types/types";
import { AssetTypeOptions } from "../../../options/options";
import { apiFetch } from "../../../services/apiClient";

export interface AssetEditModalProps {
  isOpen: boolean;
  asset: AssetTableItem | null;
  /** Flat list of all assets for parent selection (excluding the current asset). */
  allAssets: AssetTableItem[];
  currentDescription: string;
  currentDmNotes: string;
  currentTags: string[];
  onClose: () => void;
  /** Called after a successful save with the updated asset data. */
  onSaved: (_updated: AssetTableItem) => void;
}

interface EditForm {
  name: string;
  type: string;
  category: string;
  parentId: string;
}

const AssetEditModal: React.FC<AssetEditModalProps> = ({
  isOpen,
  asset,
  allAssets,
  currentDescription,
  currentDmNotes,
  currentTags,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<EditForm>({
    name: "",
    type: "",
    category: "",
    parentId: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when modal opens or asset changes
  useEffect(() => {
    if (isOpen && asset) {
      setForm({
        name: asset.name ?? "",
        type: asset.type ?? "",
        category: asset.category ?? "",
        parentId: asset.parentId != null ? String(asset.parentId) : "",
      });
      setError(null);
    }
  }, [isOpen, asset]);

  const set =
    (field: keyof EditForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!form.type) {
      setError("Type is required.");
      return;
    }
    if (!form.category.trim()) {
      setError("Category is required.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch("/api/protected/assets/asset", {
        method: "PUT",
        body: JSON.stringify({
          id: asset.id,
          name: form.name.trim(),
          type: form.type,
          category: form.category.trim(),
          description: currentDescription,
          dmNotes: currentDmNotes,
          tags: currentTags,
          parentId: form.parentId ? parseInt(form.parentId) : null,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || "Failed to save asset");
      }

      const updated: AssetTableItem = {
        ...asset,
        name: form.name.trim(),
        type: form.type,
        category: form.category.trim(),
        parentId: form.parentId ? parseInt(form.parentId) : undefined,
      };
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  // Available parents: exclude current asset and any descendants to avoid cycles.
  const parentOptions = asset
    ? (() => {
        const childrenByParent = new Map<number, number[]>();
        for (const candidate of allAssets) {
          if (candidate.parentId != null) {
            const siblings = childrenByParent.get(candidate.parentId) ?? [];
            siblings.push(candidate.id);
            childrenByParent.set(candidate.parentId, siblings);
          }
        }

        const invalidIds = new Set<number>();
        const stack: number[] = [asset.id];
        while (stack.length > 0) {
          const currentId = stack.pop();
          if (currentId === undefined || invalidIds.has(currentId)) continue;
          invalidIds.add(currentId);
          const children = childrenByParent.get(currentId) ?? [];
          for (const childId of children) stack.push(childId);
        }

        return allAssets.filter((a) => !invalidIds.has(a.id));
      })()
    : allAssets;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-amber-400/15 border border-amber-400/30 rounded flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faPen} className="text-amber-400 text-xs" />
          </div>
          <h2 className="font-semibold text-base">Edit Entry</h2>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-900/30 border border-red-600/40 rounded text-red-300 text-xs">
                <FontAwesomeIcon icon={faXmark} className="flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder="Entry name"
                required
                autoFocus
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
              />
            </div>

            {/* Type + Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.type}
                  onChange={set("type")}
                  required
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400/50"
                >
                  {AssetTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  Category <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.category}
                  onChange={set("category")}
                  placeholder="e.g. Dungeon, Noble…"
                  required
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                />
              </div>
            </div>

            {/* Parent Asset */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Parent Asset</label>
              <select
                value={form.parentId}
                onChange={set("parentId")}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400/50"
              >
                <option value="">— None (root) —</option>
                {parentOptions.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.name} ({a.type})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <span className="text-xs text-zinc-500">
            Fields marked <span className="text-red-400">*</span> are required
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-amber-400 text-zinc-950 text-sm font-bold rounded hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AssetEditModal;
